
import axios from 'axios';

import {
  defineComponent,
  ref,
} from 'vue';

import { useI18n } from 'vue-i18n';

import { useToast } from 'primevue/usetoast';

import PrimeVueDialog from 'primevue/dialog';

import PrimeVueInputText from 'primevue/inputtext';

import PrimeVueButton from 'primevue/button';

import PrimeVueTag from 'primevue/tag';

import PrimeVueSkeleton from 'primevue/skeleton';

import useFolderActions from '@/composable/useFolderActions';

import type { Folder } from '@/types';

/**
 * Данные API плюс локальные флаги строки.
 */
interface FolderRow extends Folder {
  saving?: boolean
  error?: string
  savedName?: string
  confirming?: boolean
}

/**
 * Создание, переименование, удаление папок. Удаление снимает только раскладку, сами сущности
 * не затрагиваются.
 */
export default defineComponent({
  components: {
    PrimeVueDialog,
    PrimeVueInputText,
    PrimeVueButton,
    PrimeVueTag,
    PrimeVueSkeleton,
  },
  props: {
    folderableType: {
      type: String,
      default: 'campaign',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'changed',
    'deleted',
  ],
  setup(props, { emit }) {
    const folderActions = useFolderActions();

    const i18n = useI18n();

    const toast = useToast();

    const visible = ref(false);

    const loading = ref(false);

    const folders = ref<FolderRow[]>([]);

    const newFolderName = ref('');

    const creating = ref(false);

    const createError = ref('');

    const getValidationError = (err: unknown): string => (
      axios.isAxiosError(err) && err.response?.status === 422
        ? Object.values(err.response.data.errors ?? {}).flat().join(' ')
        : String(err)
    );

    const fetchFolders = async (): Promise<void> => {
      loading.value = true;

      try {
        const response = await folderActions.fetchFolders(props.folderableType);

        // savedName хранит последнее сохранённое имя, чтобы blur без правок не дёргал API.
        folders.value = response.data.data.map((folder: Folder) => ({
          ...folder,
          savedName: folder.name,
        }));
      } catch (err) {
        console.error(err);
      }

      loading.value = false;
    };

    const open = (): void => {
      visible.value = true;

      fetchFolders();
    };

    const createFolder = async (): Promise<void> => {
      if (!newFolderName.value || creating.value) {
        return;
      }

      creating.value = true;

      createError.value = '';

      try {
        const response = await folderActions.createFolder(props.folderableType, newFolderName.value);

        folders.value = [
          ...folders.value,
          {
            ...response.data,
            campaigns_count: 0,
            savedName: response.data.name,
          },
        ].sort((a, b) => a.name.localeCompare(b.name));

        newFolderName.value = '';

        emit('changed');
      } catch (err) {
        createError.value = getValidationError(err);
      }

      creating.value = false;
    };

    // По идентификатору, а не по самой строке: иначе правки уходили бы в свойства параметра
    // функции (no-param-reassign).
    const findFolder = (folderId: number): FolderRow | undefined => folders.value.find((item) => item.id === folderId);

    const renameFolder = async (folderId: number): Promise<void> => {
      const folder = findFolder(folderId);

      if (!folder || folder.saving || folder.name === folder.savedName) {
        return;
      }

      // Пустое имя не отправляем: сервер вернёт 422, а вернуть прежнее значение понятнее.
      if (!folder.name) {
        folder.name = folder.savedName ?? '';

        return;
      }

      folder.saving = true;

      folder.error = '';

      try {
        const response = await folderActions.renameFolder(folder.id, folder.name);

        folder.savedName = response.data.name;

        emit('changed');
      } catch (err) {
        folder.error = getValidationError(err);

        folder.name = folder.savedName ?? '';
      }

      folder.saving = false;
    };

    const deleteFolder = async (folderId: number): Promise<void> => {
      const folder = findFolder(folderId);

      if (!folder || folder.saving) {
        return;
      }

      // Первый клик только предупреждает.
      if (!folder.confirming) {
        folder.confirming = true;

        return;
      }

      folder.saving = true;

      try {
        await folderActions.deleteFolder(folder.id);

        folders.value = folders.value.filter((item) => item.id !== folderId);

        toast.add({
          severity: 'success',
          detail: i18n.t('success_delete'),
          life: 3000,
        });

        // Раньше `changed`: родитель должен успеть снять фильтр по удалённой папке
        // до того, как список перечитается.
        emit('deleted', folderId);

        emit('changed');
      } catch (err) {
        folder.error = getValidationError(err);

        folder.saving = false;

        folder.confirming = false;
      }
    };

    return {
      visible,
      loading,
      folders,
      newFolderName,
      creating,
      createError,
      open,
      createFolder,
      renameFolder,
      deleteFolder,
    };
  },
});
